// The agency's own space (D-59 step C, D-56): its reads from migration 0053,
// and the writes MyGaz Pro already uses (C18): rider edits, papers, the
// Mobile Money top-up through pay-topup. Every call answers for the signed-in
// dispatcher's agency only; the demo set stands in behind the "Démo" pill.
import { supabase } from './supabase';
import { demoAgencyDay, demoAgencyHome, demoAgencyMe, demoAgencyOrders, demoMarkHandover, demoStatement } from './demo-agences';
import type { AgencyDay, AgencyHome, AgencyMe, AgencyOrder, DocKind, Handover, Provider, Statement, TopupState } from './types';

export interface RiderInput { name: string; phone: string; plate: string | null; vehicle: 'moto' | 'car'; can_install: boolean }

export interface AgencySource {
  demo: boolean;
  me(): Promise<AgencyMe | null>;
  home(): Promise<AgencyHome>;
  day(): Promise<AgencyDay>;
  statement(): Promise<Statement>;
  orders(scope: 'open' | 'ended'): Promise<AgencyOrder[]>;
  markHandover(rider: string, done: boolean): Promise<Handover[]>;
  /** Starts a top-up: pawaPay sends the PIN prompt to the paying phone (C18, D-46, D-47; D-52 keeps real money closed). */
  topup(amount: number, provider: Provider, phone: string): Promise<TopupState>;
  topupCheck(id: string): Promise<TopupState>;
  addRider(r: RiderInput): Promise<void>;
  updateRider(id: string, r: RiderInput): Promise<void>;
  /** Puts a photo in the private bucket `rider-docs`, then records it (dispatch_set_rider_document, 0036). */
  uploadDoc(agency: string, rider: string, kind: DocKind, file: File): Promise<void>;
}

async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  if (!supabase) throw new Error('no_backend');
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

// pay-topup answers {error} with the server's own word (payments_off, topup_in_flight, invalid_phone…).
async function payTopup(body: Record<string, unknown>): Promise<TopupState> {
  if (!supabase) throw new Error('no_backend');
  const { data, error } = await supabase.functions.invoke<TopupState>('pay-topup', { body });
  if (!error && data) return data;
  const res = (error as { context?: Response } | null)?.context;
  if (res instanceof Response) {
    const b = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(b?.error ?? 'http_' + res.status);
  }
  throw new Error(error?.message ?? 'payment_failed');
}

const DOC_TYPES: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };

export const liveAgency: AgencySource = {
  demo: false,
  me: () => rpc<AgencyMe | null>('agency_me'),
  home: () => rpc<AgencyHome>('agency_home'),
  day: () => rpc<AgencyDay>('agency_day'),
  statement: () => rpc<Statement>('agency_statement', { p_days: 14 }),
  orders: (scope) => rpc<AgencyOrder[]>('dispatch_orders', { p_scope: scope }),
  markHandover: (rider, done) => rpc<Handover[]>('agency_mark_handover', { p_rider: rider, p_done: done }),
  topup: (amount, provider, phone) => payTopup({ action: 'start', amount, provider, phone }),
  topupCheck: (id) => payTopup({ action: 'check', topup_id: id }),
  addRider: async (r) => { await rpc('dispatch_add_rider', { p_name: r.name, p_phone: r.phone, p_plate: r.plate, p_vehicle: r.vehicle, p_can_install: r.can_install }); },
  updateRider: async (id, r) => { await rpc('dispatch_update_rider', { p_rider: id, p_name: r.name, p_phone: r.phone, p_plate: r.plate, p_vehicle: r.vehicle, p_can_install: r.can_install }); },
  uploadDoc: async (agency, rider, kind, file) => {
    if (!supabase) throw new Error('no_backend');
    if (!DOC_TYPES[file.type]) throw new Error('bad_type');
    if (file.size > 5 * 1024 * 1024) throw new Error('too_big');
    // {agency}/{rider}/{kind}-{n}, as the bucket's rule asks (0036).
    const path = `${agency}/${rider}/${kind}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    const up = await supabase.storage.from('rider-docs').upload(path, file, { contentType: file.type, upsert: false });
    if (up.error) throw new Error(up.error.message);
    try {
      const res = await rpc<{ replaced: string | null }>('dispatch_set_rider_document', { p_rider: rider, p_kind: kind, p_path: path });
      if (res.replaced) await supabase.storage.from('rider-docs').remove([res.replaced]);
    } catch (e) {
      await supabase.storage.from('rider-docs').remove([path]);
      throw e;
    }
  },
};

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
let demoTop: TopupState | null = null;
export const demoAgency: AgencySource = {
  demo: true,
  me: async () => demoAgencyMe(),
  home: async () => demoAgencyHome(),
  day: async () => demoAgencyDay(),
  statement: async () => demoStatement(),
  orders: async (scope) => demoAgencyOrders(scope),
  markHandover: async (rider, done) => demoMarkHandover(rider, done),
  topup: async (amount, provider) => {
    await wait(500);
    demoTop = { topup_id: 'demo-top', amount_xaf: amount, provider, env: 'sandbox', status: 'accepted', failure_code: null, balance: null, created_at: new Date().toISOString() };
    return demoTop;
  },
  topupCheck: async () => {
    await wait(800);
    if (!demoTop) throw new Error('no_topup');
    demoTop = { ...demoTop, status: 'completed', balance: 6800 + demoTop.amount_xaf };
    return demoTop;
  },
  addRider: async () => { await wait(300); },
  updateRider: async () => { await wait(300); },
  uploadDoc: async () => { await wait(400); },
};
